import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { CreateExpenseSchema, CreateExpenseInput } from '@hostel-manager/shared';
import { Expense, ExpenseCategory } from '@hostel-manager/shared';
import { useFormValidation } from '../../hooks/useFormValidation';
import toast from 'react-hot-toast';

const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  'electricity',
  'water',
  'salary',
  'maintenance',
  'supplies',
  'cleaning',
  'repairs',
  'equipment',
  'refund',
  'advertising',
  'security',
  'other',
];

export interface ExpenseFormProps {
  expense?: Expense | null;
  onSubmit: (data: CreateExpenseInput) => Promise<void>;
  onCancel: () => void;
}

const getInitialFormState = () => ({
  amount: 0,
  date: new Date().toISOString().split('T')[0],
  category: 'other' as ExpenseCategory,
  description: '',
  paymentMethod: 'Cash' as 'Online' | 'Cash',
  receiptReference: '',
  monthAllocation: new Date().toISOString().substring(0, 7),
  approvedBy: '',
  tags: [] as string[],
});

/**
 * Expense form with client-side validation.
 * Uses shared Zod schema for consistent validation with server (FR-034).
 */
const ExpenseForm: React.FC<ExpenseFormProps> = ({ expense, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState(getInitialFormState);
  const [submitting, setSubmitting] = useState(false);
  const { validate } = useFormValidation(CreateExpenseSchema, {
    showToastOnError: true,
    toastDuration: 5000,
  });

  useEffect(() => {
    if (expense) {
      setFormData({
        amount: expense.amount,
        date: expense.date instanceof Date ? expense.date.toISOString().split('T')[0] : String(expense.date).split('T')[0],
        category: expense.category,
        description: expense.description,
        paymentMethod: expense.paymentMethod,
        receiptReference: expense.receiptReference || '',
        monthAllocation: expense.monthAllocation,
        approvedBy: expense.approvedBy || '',
        tags: expense.tags || [],
      });
    } else {
      setFormData(getInitialFormState());
    }
  }, [expense]);

  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: typeof value === 'number' ? value : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const dataToValidate = {
      ...formData,
      amount: Number(formData.amount),
    };

    const result = validate(dataToValidate);
    if (!result.valid || !result.data) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(result.data);
      toast.success(expense ? 'Expense updated successfully' : 'Expense added successfully');
      setFormData(getInitialFormState());
      onCancel();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save expense');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Amount (₹)"
            type="number"
            value={formData.amount}
            onChange={handleChange('amount')}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Date"
            type="date"
            value={formData.date}
            onChange={handleChange('date')}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category}
              label="Category"
              onChange={(e) => setFormData({ ...formData, category: e.target.value as ExpenseCategory })}
            >
              {EXPENSE_CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={formData.paymentMethod}
              label="Payment Method"
              onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as 'Online' | 'Cash' })}
            >
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Online">Online</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Month Allocation"
            value={formData.monthAllocation}
            onChange={handleChange('monthAllocation')}
            placeholder="YYYY-MM"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Receipt Reference"
            value={formData.receiptReference}
            onChange={handleChange('receiptReference')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Approved By"
            value={formData.approvedBy}
            onChange={handleChange('approvedBy')}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 2, justifyContent: 'flex-end' }}>
        <Grid item>
          <Button onClick={onCancel} disabled={submitting} sx={{ minHeight: 44 }}>
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !formData.description || formData.amount <= 0}
            sx={{ minHeight: 44 }}
          >
            {submitting ? 'Saving...' : expense ? 'Update Expense' : 'Add Expense'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ExpenseForm;
